(function () {
  "use strict";

  // ============================================
  // STATE MANAGEMENT
  // ============================================
  const BundleState = {
    variantData: {},

    init() {
      this.loadVariantData();
    },

    loadVariantData() {
      document.querySelectorAll("[data-variant-data]").forEach((script) => {
        const instanceId = script.dataset.variantData;
        this.variantData[instanceId] = JSON.parse(script.textContent);
      });
    },

    getVariantData(instanceId) {
      return this.variantData[instanceId];
    },
  };

  // ============================================
  // OPTION SELECTION UTILITIES
  // ============================================
  const OptionSelector = {
    getSelectedOptions(instanceId) {
      const selectedOptions = {};
      const swatchGroups = document.querySelectorAll(
        `.bundle-option-swatches[data-bundle-instance="${instanceId}"]`
      );

      swatchGroups.forEach((group) => {
        const position = parseInt(group.dataset.optionPosition);
        const checkedRadio = group.querySelector('input[type="radio"]:checked');
        if (checkedRadio) {
          selectedOptions[position] = checkedRadio.value;
        }
      });

      return selectedOptions;
    },

    selectOption(instanceId, optionPosition, optionValue) {
      const swatchGroup = document.querySelector(
        `.bundle-option-swatches[data-bundle-instance="${instanceId}"][data-option-position="${optionPosition}"]`
      );

      if (!swatchGroup) return false;

      const targetSwatch = swatchGroup.querySelector(
        `.option-swatch[data-option-value="${CSS.escape(optionValue)}"]`
      );

      if (!targetSwatch) return false;

      const radio = targetSwatch.querySelector('input[type="radio"]');
      if (!radio) return false;

      radio.checked = true;
      this.updateSwatchUI(swatchGroup, targetSwatch, optionValue);

      return true;
    },

    updateSwatchUI(swatchGroup, activeSwatch, optionValue) {
      // Update active state
      swatchGroup
        .querySelectorAll(".option-swatch")
        .forEach((el) => el.classList.remove("active"));
      activeSwatch.classList.add("active");

      // Update label
      const optionName = swatchGroup.dataset.optionName;
      const instanceId = swatchGroup.dataset.bundleInstance;
      const labelId = `selected-${optionName
        .toLowerCase()
        .replace(/\s+/g, "-")}-${instanceId}`;
      const label = document.getElementById(labelId);

      if (label) {
        label.textContent = optionValue;
      }
    },
  };

  // ============================================
  // VARIANT MATCHING & AVAILABILITY
  // ============================================
  const VariantMatcher = {
    findVariantId(instanceId, selectedOptions) {
      const variantData = BundleState.getVariantData(instanceId);
      if (!variantData) return null;

      const matchingVariant = variantData.variants.find((variant) =>
        this.variantMatchesOptions(variant, selectedOptions)
      );

      return matchingVariant ? matchingVariant.id : null;
    },

    findFirstAvailableVariant(instanceId) {
      const variantData = BundleState.getVariantData(instanceId);
      if (!variantData) return null;

      return variantData.variants.find((variant) => variant.available) || null;
    },

    variantMatchesOptions(variant, selectedOptions) {
      return Object.keys(selectedOptions).every(
        (position) =>
          variant.options[position - 1] === selectedOptions[position]
      );
    },

    isOptionAvailable(instanceId, optionPosition, optionValue) {
      const variantData = BundleState.getVariantData(instanceId);
      if (!variantData) return true;

      const currentSelections = OptionSelector.getSelectedOptions(instanceId);

      return variantData.variants.some((variant) => {
        if (!variant.available) return false;
        if (variant.options[optionPosition - 1] !== optionValue) return false;

        // Check if variant matches all other selected options
        for (let pos in currentSelections) {
          if (parseInt(pos) !== optionPosition) {
            if (variant.options[pos - 1] !== currentSelections[pos]) {
              return false;
            }
          }
        }

        return true;
      });
    },

    isCurrentSelectionAvailable(instanceId) {
      const selectedOptions = OptionSelector.getSelectedOptions(instanceId);
      const variantData = BundleState.getVariantData(instanceId);

      if (!variantData) return true;

      // Check if all options are selected
      if (Object.keys(selectedOptions).length !== variantData.options.length) {
        return false;
      }

      const matchingVariant = variantData.variants.find((variant) =>
        this.variantMatchesOptions(variant, selectedOptions)
      );

      return matchingVariant ? matchingVariant.available : false;
    },
  };

  // ============================================
  // UI UPDATES
  // ============================================
  const UIUpdater = {
    updateOptionAvailability(instanceId) {
      const swatchGroups = document.querySelectorAll(
        `.bundle-option-swatches[data-bundle-instance="${instanceId}"]`
      );

      swatchGroups.forEach((group) => {
        const optionPosition = parseInt(group.dataset.optionPosition);

        group.querySelectorAll(".option-swatch").forEach((swatch) => {
          const optionValue = swatch.dataset.optionValue;
          const isAvailable = VariantMatcher.isOptionAvailable(
            instanceId,
            optionPosition,
            optionValue
          );

          swatch.classList.toggle("unavailable", !isAvailable);
        });
      });
    },

    updateVariantImage(instanceId, updateSticky = true) {
      const selectedOptions = OptionSelector.getSelectedOptions(instanceId);
      const variantData = BundleState.getVariantData(instanceId);

      if (!variantData) return;

      const matchingVariant = variantData.variants.find((variant) =>
        VariantMatcher.variantMatchesOptions(variant, selectedOptions)
      );

      // Update bundle item image
      const mainImage = document.querySelector(
        `.bundle-item-image img[data-product-image="${instanceId}"]`
      );

      if (mainImage) {
        let newImageSrc = null;
        let highResImageSrc = null;

        if (matchingVariant && matchingVariant.featured_image) {
          // Use 400x400 for bundle item thumbnail
          mainImage.src = matchingVariant.featured_image;
          newImageSrc = matchingVariant.featured_image;

          // Get high-res version for sticky preview (2000px width)
          highResImageSrc = matchingVariant.featured_image.replace(
            "_400x400",
            "_2000x"
          );
        } else {
          mainImage.src = mainImage.dataset.defaultImage;
          newImageSrc = mainImage.dataset.defaultImage;

          // Get high-res version of default image
          highResImageSrc = mainImage.dataset.defaultImage.replace(
            "_400x400",
            "_2000x"
          );
        }

        // Only update sticky preview if flag is true (for first item or on user interaction)
        if (updateSticky) {
          console.log(
            "Updating sticky preview with high-res:",
            highResImageSrc
          );
          this.updateStickyPreview(highResImageSrc);
        }
      }
    },

    updateStickyPreview(imageSrc) {
      if (!imageSrc) {
        console.log("No image src provided to updateStickyPreview");
        return;
      }

      // Find sticky preview container
      const stickyPreview = document.querySelector(
        ".product__media[data-sticky-preview]"
      );

      if (!stickyPreview) {
        console.log("Sticky preview container not found");
        return;
      }

      console.log("Sticky preview container found");

      // Find img tag inside
      const stickyImage = stickyPreview.querySelector("img");

      if (!stickyImage) {
        console.log("Sticky preview img not found");
        return;
      }

      console.log("Updating sticky image src from:", stickyImage.src);
      console.log("To:", imageSrc);

      // Update the src with high-res image
      stickyImage.src = imageSrc;

      // Update srcset for responsive images
      if (stickyImage.hasAttribute("srcset")) {
        // Generate srcset with multiple sizes
        const baseUrl = imageSrc.split("?")[0]; // Remove query params
        const queryParams = imageSrc.includes("?")
          ? "?" + imageSrc.split("?")[1]
          : "";

        const srcsetSizes = [
          200, 300, 400, 500, 600, 700, 800, 1000, 1200, 1400, 1600, 1800, 2000,
        ];
        const srcsetArray = srcsetSizes.map((size) => {
          const sizedUrl = baseUrl.replace(/_\d+x/, `_${size}x`) + queryParams;
          return `${sizedUrl} ${size}w`;
        });

        stickyImage.srcset = srcsetArray.join(", ");
      }

      console.log("Sticky image updated successfully");
    },

    updateLineItemProperties(instanceId) {
      const propertyInput = document.getElementById(
        `bundle-property-${instanceId}`
      );

      if (!propertyInput) return;

      const swatchGroups = document.querySelectorAll(
        `.bundle-option-swatches[data-bundle-instance="${instanceId}"]`
      );
      const selectedValues = [];

      swatchGroups.forEach((group) => {
        const checkedRadio = group.querySelector('input[type="radio"]:checked');
        if (checkedRadio) {
          selectedValues.push(checkedRadio.value);
        }
      });

      propertyInput.value = selectedValues.join(" / ");

      this.rebuildCombinedRef();
    },

    rebuildCombinedRef() {
      const combinedRef = document.getElementById('bundle-ref-combined');
      if (!combinedRef) return;

      const variantIds = [];

      document.querySelectorAll('.bundle-item').forEach(function(bundleItem) {
        const iid = bundleItem.dataset.bundleInstance;
        const selectedOptions = OptionSelector.getSelectedOptions(iid);
        const variantData = BundleState.getVariantData(iid);
        if (!variantData) return;

        // Skip items with incomplete selections so _ref stays clean
        if (Object.keys(selectedOptions).length < variantData.options.length) return;

        const matchingVariant = variantData.variants.find((variant) =>
          VariantMatcher.variantMatchesOptions(variant, selectedOptions)
        );

        if (matchingVariant) {
          const price = matchingVariant.price || '0';
          variantIds.push(`${matchingVariant.id}-${price}`);
        }
      });

      combinedRef.value = variantIds.join(',');

    },

    showAllOptions(instanceId) {
      const swatchGroups = document.querySelectorAll(
        `.bundle-option-swatches[data-bundle-instance="${instanceId}"]`
      );
      const labels = document.querySelectorAll(
        `[data-option-label^="${instanceId}-"]`
      );

      swatchGroups.forEach((group) => group.classList.remove("hidden"));
      labels.forEach((label) => label.classList.remove("hidden"));
    },

    markAsSoldOut(instanceId) {
      const swatchGroups = document.querySelectorAll(
        `.bundle-option-swatches[data-bundle-instance="${instanceId}"]`
      );
      const labels = document.querySelectorAll(
        `[data-option-label^="${instanceId}-"]`
      );

      swatchGroups.forEach((group) => group.classList.add("hidden"));

      labels.forEach((label) => {
        const span = label.querySelector(".selected-option-name");
        if (span) {
          span.innerHTML = '<span class="sold-out">Sold Out</span>';
        }
      });
    },
  };

  // ============================================
  // AUTO-SELECTION LOGIC
  // ============================================
  const AutoSelector = {
    selectFirstAvailableVariant(instanceId) {
      const firstAvailableVariant =
        VariantMatcher.findFirstAvailableVariant(instanceId);

      if (!firstAvailableVariant) return false;

      const variantData = BundleState.getVariantData(instanceId);
      if (!variantData) return false;

      let allSelected = true;

      variantData.options.forEach((option) => {
        const optionValue = firstAvailableVariant.options[option.position - 1];
        const selected = OptionSelector.selectOption(
          instanceId,
          option.position,
          optionValue
        );

        if (!selected) allSelected = false;
      });

      return allSelected;
    },

    selectFirstAvailableOption(instanceId, optionPosition) {
      const variantData = BundleState.getVariantData(instanceId);
      if (!variantData) return false;

      const optionData = variantData.options.find(
        (opt) => opt.position === optionPosition
      );
      if (!optionData) return false;

      const currentSelections = OptionSelector.getSelectedOptions(instanceId);

      // Find first available value for this option
      for (let value of optionData.values) {
        const testOptions = { ...currentSelections, [optionPosition]: value };

        // Check if any variant matches this combination
        const isAvailable = variantData.variants.some((variant) => {
          if (!variant.available) return false;

          return Object.keys(testOptions).every(
            (pos) => variant.options[pos - 1] === testOptions[pos]
          );
        });

        if (isAvailable) {
          return OptionSelector.selectOption(instanceId, optionPosition, value);
        }
      }

      return false;
    },

    updateDependentOptions(instanceId, changedPosition) {
      const variantData = BundleState.getVariantData(instanceId);
      if (!variantData) return;

      variantData.options.forEach((option) => {
        if (option.position === changedPosition) return;

        const currentSelection =
          OptionSelector.getSelectedOptions(instanceId)[option.position];

        if (
          currentSelection &&
          !VariantMatcher.isOptionAvailable(
            instanceId,
            option.position,
            currentSelection
          )
        ) {
          this.selectFirstAvailableOption(instanceId, option.position);
        }
      });
    },
  };

  // ============================================
  // ADD TO CART BUTTON MANAGEMENT
  // ============================================
  const CartButton = {
    button: null,
    buttonText: null,
    originalText: "",
    stickyBtn: null,
    stickyTextSpan: null,
    stickyOriginalText: "",

    init() {
      this.button = document.querySelector(".product-form__submit");
      if (this.button) {
        this.buttonText = this.button.querySelector(".btn-text");
        if (this.buttonText) {
          this.originalText = this.buttonText.textContent.trim();
        }
      }

      // Sticky ATC button — text is in [data-sold-out] span (hidden on mobile)
      this.stickyBtn = document.querySelector("product-sticky-form button[type='submit']");
      if (this.stickyBtn) {
        this.stickyTextSpan = this.stickyBtn.querySelector("[data-sold-out]");
        if (this.stickyTextSpan) {
          this.stickyOriginalText = this.stickyTextSpan.textContent.trim();
        }
      }
    },

    checkAllItemsComplete() {
      const bundleItems = document.querySelectorAll(".bundle-item");
      return Array.from(bundleItems).every((item) => {
        const instanceId = item.dataset.bundleInstance;
        const selectedOptions = OptionSelector.getSelectedOptions(instanceId);
        const variantData = BundleState.getVariantData(instanceId);
        if (!variantData) return false;
        return Object.keys(selectedOptions).length === variantData.options.length;
      });
    },

    checkAllBundleItems() {
      const bundleItems = document.querySelectorAll(".bundle-item");

      return Array.from(bundleItems).every((item) => {
        const instanceId = item.dataset.bundleInstance;
        return VariantMatcher.isCurrentSelectionAvailable(instanceId);
      });
    },

    update() {
      if (!this.button) return;

      if (!this.checkAllItemsComplete()) {
        this.promptSelection();
      } else if (this.checkAllBundleItems()) {
        this.enable();
      } else {
        this.disable();
      }
    },

    enable() {
      this.button.disabled = false;
      this.button.classList.remove("sold-out");
      if (this.buttonText && this.originalText) {
        this.buttonText.textContent = this.originalText;
      }

      if (this.stickyBtn) {
        this.stickyBtn.disabled = false;
        if (this.stickyTextSpan && this.stickyOriginalText) {
          this.stickyTextSpan.textContent = this.stickyOriginalText;
        }
      }
    },

    disable() {
      this.button.disabled = true;
      this.button.classList.add("sold-out");
      if (this.buttonText) {
        this.buttonText.textContent = "Sold Out";
      }

      if (this.stickyBtn) {
        this.stickyBtn.disabled = true;
        if (this.stickyTextSpan) this.stickyTextSpan.textContent = "Sold Out";
      }
    },

    promptSelection() {
      this.button.disabled = true;
      this.button.classList.remove("sold-out");
      if (this.buttonText) {
        this.buttonText.textContent = "Choose your options";
      }

      if (this.stickyBtn) {
        this.stickyBtn.disabled = true;
        if (this.stickyTextSpan) this.stickyTextSpan.textContent = "Choose options";
      }
    },
  };

  // ============================================
  // EVENT HANDLERS
  // ============================================
  const EventHandlers = {
    handleOptionChange(event) {
      const radio = event.target;
      const swatch = radio.closest(".option-swatch");
      const group = radio.closest(".bundle-option-swatches");

      if (!group || !swatch) return;

      const instanceId = group.dataset.bundleInstance;
      const optionPosition = parseInt(group.dataset.optionPosition);
      const selectedValue = radio.value;

      // Update UI
      OptionSelector.updateSwatchUI(group, swatch, selectedValue);

      // Update dependent options
      AutoSelector.updateDependentOptions(instanceId, optionPosition);

      // Update availability states
      UIUpdater.updateOptionAvailability(instanceId);

      // Update image (always update sticky on user interaction)
      UIUpdater.updateVariantImage(instanceId, true);

      // Update hidden properties
      UIUpdater.updateLineItemProperties(instanceId);

      // Update cart button
      CartButton.update();
    },

    attachEventListeners() {
      document
        .querySelectorAll('.bundle-option-swatches input[type="radio"]')
        .forEach((radio) => {
          radio.addEventListener("change", this.handleOptionChange);
        });
    },
  };

  // ============================================
  // INITIALIZATION
  // ============================================
  const BundleManager = {
    init() {
      // Initialize state
      BundleState.init();
      CartButton.init();

      // Get all bundle items
      const bundleItems = document.querySelectorAll(".bundle-item");

      // Initialize each bundle item
      bundleItems.forEach((bundleItem) => {
        const instanceId = bundleItem.dataset.bundleInstance;
        this.initializeBundleItem(instanceId);
      });

      // Attach event listeners
      EventHandlers.attachEventListeners();

      // Initial button state
      CartButton.update();
    },

    initializeBundleItem(instanceId) {
      const variantData = BundleState.getVariantData(instanceId);
      if (!variantData) return;

      const hasAvailableVariants = variantData.variants.some((v) => v.available);

      if (hasAvailableVariants) {
        UIUpdater.showAllOptions(instanceId);
        UIUpdater.updateOptionAvailability(instanceId);

        // Clear Liquid-rendered label values — no pre-selection on load
        variantData.options.forEach((option) => {
          const labelId = `selected-${option.name.toLowerCase().replace(/\s+/g, "-")}-${instanceId}`;
          const label = document.getElementById(labelId);
          if (label) label.textContent = "";
        });
      } else {
        UIUpdater.markAsSoldOut(instanceId);
      }

      UIUpdater.updateLineItemProperties(instanceId);
    },
  };

  // ============================================
  // START APPLICATION
  // ============================================
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => BundleManager.init());
  } else {
    BundleManager.init();
  }

  // The theme dispatches 'variant:change' AFTER calling toggleSubmitButton() on every
  // variant change. By listening here we are guaranteed to run after the theme has
  // re-enabled the button, so our bundle-aware state always wins.
  const variantChangeTarget = document.querySelector('[data-type="add-to-cart-form"]') || document;
  variantChangeTarget.addEventListener('variant:change', function() {
    CartButton.update();
  });

    // Guard: block cart submission if _ref is empty (catches JS load failures)
  const refInput = document.getElementById('bundle-ref-combined');
  const submitBtn = document.querySelector('.product-form__submit');

  if (refInput && submitBtn) {
    submitBtn.addEventListener('click', function(e) {
      // Always rebuild fresh before submission
      UIUpdater.rebuildCombinedRef();

      if (refInput.value) return; // all good — let through

      // _ref empty after rebuild — JS initialization failure
      e.preventDefault();
      e.stopImmediatePropagation();

      var btnText = submitBtn.querySelector('.btn-text');
      var originalText = btnText ? btnText.textContent : '';
      submitBtn.disabled = true;
      if (btnText) btnText.textContent = 'Error — Please Refresh';

      setTimeout(function() {
        submitBtn.disabled = false;
        if (btnText) btnText.textContent = originalText;
      }, 4000);
    }, true); // capture phase: runs before the theme's Ajax cart listener
  }

})();